/**
 * " S7M517Z8 - EYM " → "S7M517Z8-EYM"
 * Özel kod ve renk kodu '-' ile ayrılır; tüm boşluklar (baş, son, orta) yok sayılır.
 * KeyCode satırlarında tire yoktur (ör. S7M517Z8EYM).
 */
export const normalizeSearchCode = (code: string): string => code.trim().replace(/\s+/g, '');

export const parseSearchCodesFromText = (text: string): string[] =>
    text
        .split('\n')
        .map((line) => normalizeSearchCode(line))
        .filter((line) => line.length > 0);

/** Toplu arama üst limiti (sayfa + modal ortak). */
export const BATCH_SEARCH_LIMIT = 100;

export const IMAGE_SEARCH_V2_URL_KEYS = {
    searchType: 'searchType',
    codes: 'codes',
} as const;

export type ImageSearchTypeValue = 1 | 2;

export interface ImageSearchV2UrlState {
    searchType: ImageSearchTypeValue;
    codes: string[];
    searchedText: string;
}

export const buildImageSearchV2UrlSearch = (searchType: ImageSearchTypeValue, codes: string[]): string => {
    if (codes.length === 0) {
        return '';
    }

    const params = new URLSearchParams();
    params.set(IMAGE_SEARCH_V2_URL_KEYS.searchType, String(searchType));
    params.set(IMAGE_SEARCH_V2_URL_KEYS.codes, codes.join(','));
    return `?${params.toString()}`;
};

export const parseImageSearchV2FromUrl = (search: string): ImageSearchV2UrlState => {
    const query = new URLSearchParams(search);
    const searchTypeRaw = Number(query.get(IMAGE_SEARCH_V2_URL_KEYS.searchType));
    const searchType: ImageSearchTypeValue = searchTypeRaw === 1 ? 1 : 2;
    const codesParam = query.get(IMAGE_SEARCH_V2_URL_KEYS.codes);
    const codes = codesParam
        ? codesParam
              .split(',')
              .map((code) => normalizeSearchCode(code))
              .filter((code) => code.length > 0)
        : [];

    return {
        searchType,
        codes,
        searchedText: codes.join('\n'),
    };
};

/** UNC (\\server\...), //server/..., drive (C:\...) veya file:// — S3 Uri üretilemez */
const isNasOrLocalPath = (path?: string | null): boolean => {
    if (!path) {
        return false;
    }

    const trimmed = path.trim();
    if (/^file:/i.test(trimmed)) {
        return true;
    }

    const normalized = trimmed.replace(/\\/g, '/');
    if (normalized.startsWith('//')) {
        return true;
    }

    return /^[a-zA-Z]:\//.test(normalized);
};

/**
 * ImageSearchV2: http(s) uri varsa onu kullan.
 * NAS/local path için boş döner (S3'e import edilmemiş eski kayıtlar).
 */
export const toBrowserImageUrl = (uri?: string | null, path?: string | null): string => {
    if (uri && /^https?:\/\//i.test(uri.trim())) {
        return uri.trim();
    }

    if (uri && !isNasOrLocalPath(uri)) {
        return uri.trim();
    }

    if (!path || isNasOrLocalPath(path)) {
        return '';
    }

    return path.trim();
};

const isAbsoluteHttpUrl = (value?: string | null): boolean => !!value && /^https?:\/\//i.test(value.trim());

/**
 * Public S3/MinIO URL pathname'inden object key çıkarır.
 * Örn: https://host/bucket/DEV/igloo_share/a.jpg → DEV/igloo_share/a.jpg
 */
const extractS3KeyFromHttpUrl = (raw: string): string | null => {
    try {
        const url = new URL(raw.trim());
        const pathname = decodeURIComponent(url.pathname).replace(/^\/+/, '');
        if (!pathname) {
            return null;
        }

        const envPattern = /(?:^|\/)((?:DEV|TEST|PROD|ONEFIXTEST)\/.+)$/i;
        const envMatch = envPattern.exec(pathname);
        if (envMatch?.[1]) {
            return envMatch[1];
        }

        const slash = pathname.indexOf('/');
        if (slash > 0) {
            return pathname.slice(slash + 1);
        }

        return pathname;
    } catch {
        return null;
    }
};

/**
 * ImageSearchV2 meta → S3 object key.
 * Modern kayıt: path = key; NAS → null; http URL → pathname'den key.
 */
export const resolveS3ObjectKey = (uri?: string | null, path?: string | null): string | null => {
    const trimmedPath = path?.trim() ?? '';
    if (
        trimmedPath &&
        !isNasOrLocalPath(trimmedPath) &&
        !isAbsoluteHttpUrl(trimmedPath) &&
        !trimmedPath.startsWith('//')
    ) {
        return trimmedPath.replace(/^\/+/, '');
    }

    const httpCandidate = [uri, path].find((v) => isAbsoluteHttpUrl(v));
    if (httpCandidate) {
        return extractS3KeyFromHttpUrl(httpCandidate);
    }

    if (uri?.trim().startsWith('//')) {
        return extractS3KeyFromHttpUrl(`https:${uri.trim()}`);
    }

    return null;
};
export const mapImageSearchV2Item = (item: any, meta: any, subIndex: number) => {
    const imageSrc = toBrowserImageUrl(meta.uri, meta.path);
    return {
        id: `${subIndex}`,
        img: item.fullName || '-',
        productName: item.productName || '-',
        specialCode: item.specialCode || '-',
        size: item.size || '-',
        colorCode: item.colorCode || '-',
        seasonCode: item.seasonCode || '-',
        merchAgeGroup: item.merchAgeGroup || '-',
        currentStatus: item.currentStatus || '-',
        line: item.line || '-',
        barcode: item.barcode || '-',
        imagePathIndexMetas: {
            id: subIndex,
            barcode: meta.barcode,
            uri: meta.uri || null,
            path: meta.path || null,
            imageSrc,
        },
    };
};

export const parseImageSearchV2ResponseData = (data: any[]) =>
    (data || [])
        .filter((item: any) => item.imagePathIndexMetas && item.imagePathIndexMetas.length > 0)
        .flatMap((item: any) =>
            item.imagePathIndexMetas.map((meta: any, subIndex: number) => mapImageSearchV2Item(item, meta, subIndex))
        );

/** Özel-Renk (tireli) satırlarda özel/renk ayrımı; KeyCode satırlarında ham değer. */
export const formatImageSearchV2NotFoundRecords = (records: string[]) =>
    records.map((item: string) => {
        const dashIndex = item.lastIndexOf('-');
        if (dashIndex > 0 && dashIndex < item.length - 1) {
            return `${item.slice(0, dashIndex)} ${item.slice(dashIndex + 1)}`;
        }

        return item;
    });
